import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import { useAuth } from "../../../app/providers/AuthProvider";
import { profileService } from "../../../services/profile";
import { formatApiError } from "../../../services/api";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { PhoneInput } from "../../../components/forms/PhoneInput";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";

/**
 * Account details section (A5-bundle item 5).
 * - No auto-save on language change; all fields stage locally.
 * - Save button is enabled iff ANY of name/phone/language differs from
 *   the persisted user snapshot. No "no changes" copy — the disabled
 *   button is the only affordance.
 * - Save fires profile PATCH (name/phone) and language PATCH in parallel
 *   only when they differ.
 */
export function AccountSection() {
  const { t, i18n } = useTranslation();
  const { user, applyUser, updateLanguage } = useAuth();

  const persisted = useMemo(
    () => ({
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
      phone: user?.phone || "",
      language: user?.language === "en" ? "en" : "de",
    }),
    [user?.first_name, user?.last_name, user?.phone, user?.language]
  );

  const [firstName, setFirstName] = useState(persisted.first_name);
  const [lastName, setLastName] = useState(persisted.last_name);
  const [phone, setPhone] = useState(persisted.phone);
  const [language, setLanguage] = useState(persisted.language);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Keep local staged state in sync when the user snapshot in context
  // changes (e.g. after Save success or a fresh /me hydrate).
  React.useEffect(() => {
    setFirstName(persisted.first_name);
    setLastName(persisted.last_name);
    setPhone(persisted.phone);
    setLanguage(persisted.language);
    setErrors({});
  }, [persisted]);

  const dirty =
    firstName !== persisted.first_name ||
    lastName !== persisted.last_name ||
    phone !== persisted.phone ||
    language !== persisted.language;

  const validate = () => {
    const errs = {};
    if (!firstName.trim()) errs.first_name = t("profile.err_required");
    if (!lastName.trim()) errs.last_name = t("profile.err_required");
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (!dirty || saving) return;
    if (!validate()) return;
    setSaving(true);
    try {
      const profileChanged =
        firstName !== persisted.first_name ||
        lastName !== persisted.last_name ||
        phone !== persisted.phone;
      const languageChanged = language !== persisted.language;

      let latest = user;
      if (profileChanged) {
        latest = await profileService.updateProfile({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          phone,
        });
        applyUser(latest);
      }
      if (languageChanged) {
        // updateLanguage() PATCHes /auth/me/language, updates the user
        // snapshot in AuthProvider, applies i18n change, and clears the
        // per-tab session override so the new persisted default wins.
        latest = await updateLanguage(language);
      }

      toast.success(i18n.t("profile.profile_saved", { lng: language }));
    } catch (err) {
      toast.error(formatApiError(err, t("common.error_generic")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={onSave}
      className="rounded-lg border border-border-subtle bg-surface p-6 shadow-card"
      data-testid="account-section"
    >
      <div className="mb-4 flex items-baseline justify-between">
        <h2 className="text-lg font-semibold text-text-primary">
          {t("profile.account_heading")}
        </h2>
        <div className="h-px w-8 bg-champagne" aria-hidden />
      </div>

      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label>{t("profile.email_label")}</Label>
          <Input
            value={user?.email || ""}
            readOnly
            disabled
            data-testid="account-email"
            className="h-10 border-border-subtle bg-offwhite text-text-secondary"
          />
          <p className="text-xs text-text-secondary">
            {t("profile.email_readonly_hint")}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="acc-first-name">{t("profile.first_name_label")}</Label>
            <Input
              id="acc-first-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              data-testid="account-first-name"
              className="h-10 bg-surface border-border-subtle focus-visible:ring-champagne"
            />
            {errors.first_name && (
              <p className="text-xs text-status-unavailable">{errors.first_name}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="acc-last-name">{t("profile.last_name_label")}</Label>
            <Input
              id="acc-last-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              data-testid="account-last-name"
              className="h-10 bg-surface border-border-subtle focus-visible:ring-champagne"
            />
            {errors.last_name && (
              <p className="text-xs text-status-unavailable">{errors.last_name}</p>
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>{t("profile.phone_label")}</Label>
          <PhoneInput
            value={phone}
            onChange={setPhone}
            testIdRoot="account-phone"
          />
        </div>

        <div className="space-y-1.5">
          <Label>{t("profile.language_label")}</Label>
          <Select value={language} onValueChange={setLanguage}>
            <SelectTrigger
              data-testid="account-language-trigger"
              className="h-10 bg-surface border-border-subtle"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="de" data-testid="account-language-de">
                Deutsch (DE)
              </SelectItem>
              <SelectItem value="en" data-testid="account-language-en">
                English (EN)
              </SelectItem>
            </SelectContent>
          </Select>
          <p
            className="text-xs text-text-secondary"
            data-testid="account-language-hint"
          >
            {t("profile.language_hint")}
          </p>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-end">
        <Button
          type="submit"
          disabled={!dirty || saving}
          data-testid="account-save"
          className="bg-charcoal text-white hover:bg-charcoal-soft focus-visible:ring-champagne"
        >
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("common.saving")}
            </>
          ) : (
            t("profile.save_changes")
          )}
        </Button>
      </div>
    </form>
  );
}
